/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import {
  faDownload,
  faExclamationTriangle,
  faSync,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../../../../common/Button';
import TranscriptViewer from '../../../components/TranscriptViewer';
import Progress from 'antd/es/progress';
import Tooltip from 'antd/es/tooltip';
import { Collapse } from 'antd';

import {
  Flex,
  Grid,
  Loading,
  PageContent,
  PermissionGuard,
  RecordingExport,
  RemoteRecordingPlayer,
  RemoteSession,
  useAssetTranslation,
  useFetch,
  useUserinfo,
} from '@mss/mss-component-library';
import Fetch from 'jsx/common/util/_fetch';
import Api from '../../../services/Api';

type OverviewProps = {
  session: RemoteSession;
};
const FILE_TRANSFER_NS = { ns: 'fileTransfer' as const };
export default function Overview({ session }: OverviewProps) {
  const { t } = useAssetTranslation();
  const [hasPermission] = useUserinfo();
  const authFormField = useRef<HTMLInputElement>(null);
  const downloadTranscriptLink = useRef<HTMLAnchorElement | null>(null);

  const [recordingExport, setRecordingExport] =
    useState<RecordingExport | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [exportProgress, setExportProgress] = useState<number>(0);
  const [resolution, setResolution] = useState<{
    width: number;
    height: number;
  }>({ width: 1280, height: 720 });

  const [fetchedExport, fetchExport] = useFetch({
    url: `/api/remote-recording-export/?session=${session.id}`,
    defaultValue: null,
    initialFetch: hasPermission('download_recordingexport'),
    onSucces: result => {
      const data: RecordingExport[] = result.data;
      if (data && data.length > 0) {
        setRecordingExport(data[0]);
      }
    },
    // TODO onFail: () => notify
  });

  useEffect(() => {
    if (!recordingExport) return;
    const { bitrate, filesize } = recordingExport;
    if (!filesize || !duration) return;
    const offset = 400000; // 400kb
    const estimateFilesize = offset + (duration * bitrate) / 8 / 1000;
    const percent = Math.round((100 / estimateFilesize) * filesize);
    setExportProgress(percent);
  }, [recordingExport, duration]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        recordingExport &&
        ['progress', 'queued'].includes(recordingExport.status)
      )
        fetchExport();
    }, 1000);
    return () => clearInterval(interval);
  }, [recordingExport, fetchExport]);

  const startExport = async () => {
    try {
      const response = await Api.session.startRecordingExport({
        session: session.id,
        ...resolution,
      });
      const recording: RecordingExport = await response.json();
      setRecordingExport(recording);
    } catch (e) {
      console.error('API error', e);
    }
  };

  const downloadForm = useRef<HTMLFormElement>(null);
  const downloadExport = async () => {
    const data: Response = await Fetch.get(
      `/api/remote-recording-export/${recordingExport?.id}/download_link/`
    );

    const downloadLink: { url: string } = await data.json();
    if (!downloadLink) return;

    const authField = authFormField.current;
    const form = downloadForm.current;
    if (!form) return;
    form.action = downloadLink.url;
    const token = sessionStorage.getItem('token') || undefined;
    authField!.value = `Bearer ${token}`;
    form.submit();
  };

  const downloadTranscript = () => {
    if (!downloadTranscriptLink?.current) return;
    if (!session.transcript || session.transcript === '') return;
    const transcriptBlob = new Blob([session.transcript], {
      type: 'text/plain',
    });
    const link = downloadTranscriptLink.current;
    link.href = URL.createObjectURL(transcriptBlob);
    link.download = 'transcript.txt';
    link.click();
    return false;
  };

  const exportButton = (status: string | undefined) => {
    if (!fetchedExport.data && fetchedExport.loading) {
      return <Loading />;
    }

    switch (status) {
      case 'queued':
      case 'progress':
        return (
          <Flex h="end" v="center">
            <div style={{ width: 170 }}>
              <Progress percent={exportProgress} size="small" />
            </div>
            <Button
              variant="primary"
              ghost
              className="ml-4"
              onClick={startExport}
              data-ta="startExportButton"
              loading={true}
            >
              <> {`${t('Processing')}...`}</>
            </Button>
          </Flex>
        );
      case 'complete':
        return (
          <form ref={downloadForm} action="/" method="post" target="_blank">
            <input type="hidden" ref={authFormField} name="authorization" />
            <Button
              onClick={downloadExport}
              type="submit"
              variant="primary"
              className="ml-4"
              data-ta="downloadExportButton"
            >
              <>
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                {t('Download recording',FILE_TRANSFER_NS)}
              </>
            </Button>
          </form>
        );
      case 'error':
        return (
          <Flex h="end" v="center">
            <Tooltip
              title={
                recordingExport?.log ? (
                  <p
                    dangerouslySetInnerHTML={{ __html: recordingExport?.log }}
                  ></p>
                ) : (
                  t('Error')
                )
              }
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mr-2 text-danger"
                size="lg"
              />
            </Tooltip>
            <Button
              variant="primary"
              ghost
              className="ml-4"
              data-ta="startExportButton"
              onClick={startExport}
            >
              <>
                <FontAwesomeIcon icon={faSync} className="mr-2" />
                {t('Retry')}
              </>
            </Button>
          </Flex>
        );
      default:
        return (
          <Button
            variant="primary"
            ghost
            className="ml-4"
            onClick={startExport}
            data-ta="startExportButton"
          >
            <>
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              {t('Export')}
            </>
          </Button>
        );
    }
  };

  return (
    <PermissionGuard codename={'view_recording_remotesession'} redirect={true}>
      <PageContent
        title={t('Recording player')}
        actions={
          <span>
            {hasPermission('download_recordingexport') &&
              exportButton(recordingExport?.status)}
          </span>
        }
      >
        <Grid.Row>
          <Grid.Col width={12}>
            <RemoteRecordingPlayer
              session={session}
              theming="site"
              onDuration={(duration: number) => setDuration(duration)}
              onResize={(width: number, height: number) =>
                setResolution({ width, height })
              }
            />
          </Grid.Col>
          {session?.transcript && (
            <Collapse
              defaultActiveKey={'false'}
              bordered={false}
              expandIconPosition="end"
              className="transcript-collapse"
            >
              <Collapse.Panel
                key="transcript"
                header={
                  <div>
                    <span>{t('Transcript')}</span>
                    <div>
                      <Button onClick={downloadTranscript}>
                        {t('Download',FILE_TRANSFER_NS)}
                      </Button>
                      <a download="" ref={downloadTranscriptLink}></a>
                    </div>
                  </div>
                }
              >
                <Grid.Row>
                  <Grid.Col width={12}>
                    <TranscriptViewer transcript={session.transcript} />
                  </Grid.Col>
                </Grid.Row>
              </Collapse.Panel>
            </Collapse>
          )}
        </Grid.Row>
      </PageContent>
    </PermissionGuard>
  );
}
